import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface DropdownOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-multi-select-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectDropdownComponent implements OnChanges {
  @Input() options: DropdownOption[] = [];
  @Input() selectedIds: string[] = [];
  @Input() placeholder: string = '👤 All items';
  @Input() icon: string = '👤';
  @Output() selectionChange = new EventEmitter<string[]>();

  isOpen: boolean = false;
  internalSelectedIds: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    // Keep internal copy in sync with input changes
    if (changes['selectedIds'] && changes['selectedIds'].currentValue) {
      this.internalSelectedIds = [...changes['selectedIds'].currentValue];
    }
  }

  /**
   * Toggle dropdown open/close
   */
  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.isOpen = false;
  }

  /**
   * Handle option selection/deselection
   */
  toggleOption(optionId: string): void {
    if (this.internalSelectedIds.includes(optionId)) {
      this.internalSelectedIds = this.internalSelectedIds.filter(id => id !== optionId);
    } else {
      this.internalSelectedIds = [...this.internalSelectedIds, optionId];
    }
    // Emit updated selection
    this.selectionChange.emit(this.internalSelectedIds);
  }

  /**
   * Check if option is selected
   */
  isSelected(optionId: string): boolean {
    return this.internalSelectedIds.includes(optionId);
  }

  /**
   * Get display label for selected items
   */
  getDisplayLabel(): string {
    if (this.internalSelectedIds.length === 0) {
      return this.placeholder;
    }
    if (this.internalSelectedIds.length === 1) {
      const selected = this.options.find(opt => opt.id === this.internalSelectedIds[0]);
      return selected ? selected.label : this.placeholder;
    }
    return `${this.internalSelectedIds.length} selected`;
  }

  /**
   * Get count of selected items
   */
  getSelectedCount(): number {
    return this.internalSelectedIds.length;
  }
}
